import { Model } from '@opaquejs/opaque'
import { makeReactive } from '@opaquejs/vue'
import Wall from './Wall'
import presets from '@/assets/js/presets'
import Summary from './Summary'
import Vue from 'vue'
import i18n from '@/i18n'

export default class Form extends makeReactive(Model) {

    static get schema() {
        return {
            id: null,
            current_step: 0,
            preset_id: this.ROOM_SHAPE.RECTANGLE,
            image: null,
            telephone: null,
            mail: null,
            kundentyp: null,
            companyname: null,
            kundennummer: null,
            anschrift: {
                name: null,
                straße: null,
                postleitzahl: null,
                city: null
            },
            formteile: {
                innenecken: 8,
                außenecken: 0,
                verbinder: 0
            },
            anmerkungen: null,
            mailCopyTo: [] // these email addresses will get a copy of the configuration
        }
    }

    static get presets() {
        if(!this._presets) {
            this._presets = Vue.observable(presets())
        }
        return this._presets
    }

    constructor() {
        super()
        this.summary = new Summary(this)
    }

    get all_walls() {
        return this.preset.walls
    }

    get walls() {
        return this.all_walls.filter(wall => wall.activated)
    }

    get isIndividual() {
        return this.preset_id == Form.ROOM_SHAPE.INDIVIDUAL
    }

    get needsCustomercode() {
        return [Form.CUSTOMER_TYPE.WHOLESALE, Form.CUSTOMER_TYPE.PROCESSOR, Form.CUSTOMER_TYPE.SPECIALIST_MARKET].includes(this.kundentyp)
    }

    get preset() {
        return this.constructor.presets[this.preset_id]
    }

    // return the preset for the given walls configuration
    get formteilePreset() {
        let result = this.preset.formteile(this.walls)
        result.verbinder = this.getDependentOnLength(this.sum(this.walls.map(wall => wall.length)), { 0: 1, 2.5: 2, 5.01: 4, 7.51: 5, 10.01: 7, 12.51: 8, 15.01: 10 })

        return result;
    }

    sum(values) {
        return values.reduce((sum, value) => sum + value, 0)
    }

    getDependentOnLength(length, values) {
        let highest = null
        for(let min in values) {
            min = parseFloat(min)
            if(highest == null || ( min > highest && min <= length )) {
                highest = min
            }
        }
        return values[highest]
    }

    nextName() {
        const names = Wall.names
        return this.all_walls.length > 0 ? names[names.indexOf(this.all_walls[this.all_walls.length - 1].name) + 1] : names[0]
    }

    addWall() {
        const name = this.nextName()
        this.all_walls.push(Wall.make({ name }))
    }

    removeWall(wall) {
        return this.all_walls.splice(this.all_walls.indexOf(wall), 1)
    }

    areWallsValid() {
        return this.walls.length > 0;
    }

    async send() {
        const response = await fetch(process.env.NODE_ENV == 'development' ? 'http://test.local/wordpress/wp-admin/admin-ajax.php?action=ledconfigurator' : 'https://www.licht-sockelleiste.de/wp-admin/admin-ajax.php?action=ledconfigurator', {
            method: 'POST',
            body: JSON.stringify(this.getMailData())
        })
        if (!response.ok) {
            throw new Error()
        }
    }

    getMailData() {
        return {
            ...this.getAttributes(),
            walls: this.walls.map(wall => wall.getAttributes()),
            locale: i18n.locale,
            summary: this.summary.getMailData()
        }
    }

    serialize() {
        const serialized_presets = {}
        for(const id in this.constructor.presets) {
            serialized_presets[id] = {
                walls: this.constructor.presets[id].walls.map(wall => wall.getAttributes())
            }
        }

        return { ...this.getAttributes(), presets: serialized_presets }
    }

    static deserialize(serialized) {
        const instance = this.make(serialized)
        
        for(const id in serialized.presets) {
            this.presets[id].walls = serialized.presets[id].walls.map(wall => Wall.make(wall))
        }

        return instance
    }
}

Form.CUSTOMER_TYPE = {
    WHOLESALE: 'customer-information.customer-type.options.wholesale',
    PROCESSOR: 'customer-information.customer-type.options.processor',
    ARCHITECT: 'customer-information.customer-type.options.architect',
    SPECIALIST_MARKET: 'customer-information.customer-type.options.specialist-market',
    OTHER: 'customer-information.customer-type.options.other',
}

Form.ROOM_SHAPE = {
    RECTANGLE: 'roomshape.shapes.rectangle',
    T: 'roomshape.shapes.t',
    U: 'roomshape.shapes.u',
    L: 'roomshape.shapes.l',
    INDIVIDUAL: 'roomshape.shapes.individual',
}